import React, { memo, useEffect, useMemo, useRef } from "react";
import truncateTime, {
  getFormattedDurationTimes,
  ONE_DAY_OF_MS,
} from "../../../../utils/timeUtils";
import CallIcon from "../../../../svgs/CallIcon";
import { useSettings } from "../../../../contexts/SettingsManager/SettingsManager";
import { getPredictedFutureMetric } from "../../../../utils/metrics";
import type { Process } from "../../../../types/types";
import {
  useForceUpdateOnInterval,
  useRollupDisplayForProcess,
  useSegmentsForProcess,
  useSelectedShift,
  useFormatZoned,
  useIsAdmin,
  useGetPageFromPath,
} from "../../../../utils/customHooks/genericHooks";
import { useFlightTranslation } from "../../../../utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useIsStorybook } from "../../../../utils/customHooks/storyHooks";
import { useCountdownTimer } from "../../../../utils/customHooks/processTileHooks";
import {
  getCallAndLastSegmentDetails,
  useExpandedTileFlags,
} from "./processTileUtils";
import { ProcessTileStyles } from "./ProcessTileStyles";
import { Button, Tooltip } from "@mui/material";
import { STORY_SHIFT_START } from "utils/storyUtils";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { LessThanOne } from "./ProcessTilesPanelStyles";
import {
  LineRolesEnum,
  TILE_WIDTH_PX_LG,
  useBreakpoints,
} from "constants/constants";
import ExpandedTileContent, {
  useExpandedTileState,
} from "./ExpandedTile/ExpandedTileContent";
import { useTileSize } from "pages/useTileSize";
import { useMapDataDumpToRollup } from "components/Dashboard/TrendChart/TrendChartContent/trendChartContentUtils";
import { useShouldUseDataDumpsForProcessTile } from "utils/customHooks/whichServerHooks";
import { useIsDarkMode } from "store/atoms";
import { PnlCategoriesEnum } from "types/enums";
import { LineRoleDecorator } from "./LineRoleDecorator";
import { ResponsiveTypography } from "@raven/shared/components";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useSelector } from "store/useSelector";
import { useLastDataDumpRowPerProcess } from "components/DataGetter/LastDataDumpRowGetter";
import { atom, useAtom } from "jotai";

const MAX_CHARACTERS_BEFORE_SHRINK = 12;
const MAX_CHARACTERS_BEFORE_LEFT_ALIGN = 15;

type ProcessTileProps = {
  process: Process;
  isAtLeastOneProcessWithLineRole?: boolean;
};
/**
 * Square tile representing one process / machine, coloured by status,
 * shows the time or whether there's a "call" on the process
 */
const ProcessTile = React.memo<ProcessTileProps>(
  ({ process, isAtLeastOneProcessWithLineRole = false }) => {
    const ckey = useCustomerKey();
    const isStorybook = useIsStorybook();
    const isDT = ckey === "dt";
    const {
      preferredMetric,
      isPredictedFutureMetric,
      estimatedFutureMetricPercent,
      areProcessTilesExpanded,
    } = useSettings();
    const shouldUseDataDumps = useShouldUseDataDumpsForProcessTile();
    const [lastDataDumpRowPerProcess] = useLastDataDumpRowPerProcess();
    const dataDumpsFullRangeByProcess = useSelector(
      state => state.legacy.dataDumpsFullRangeByProcess
    );
    const singleProcessDataDump = dataDumpsFullRangeByProcess[process.id];
    const lastSegment =
      // ! lastDataDumpRowPerProcess is empty
      // TODO disabled due to flooding midgard cache with small queries; consider redesigning around this issue; for now, use the full-range DDA.lastSegment on a slower interval instead
      lastDataDumpRowPerProcess?.[process.id] ??
      singleProcessDataDump?.lastSegment;
    const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

    const { isTilesPageFullDay } = useSettings();

    const rollupDisplay = useRollupDisplayForProcess(process.id);
    const callStartMs = lastSegment?.callStart ?? null;
    const callTarget = lastSegment?.callTarget ?? null;
    const mapDataDumpToRollup = useMapDataDumpToRollup();

    const processSegments = useSegmentsForProcess(process.id);
    const { segment, pnlCategory, metricDisplay, metricUnits, statusString } =
      getCallAndLastSegmentDetails({
        dataDumpsObjForFullRange: singleProcessDataDump,
        rollupMetric:
          shouldUseDataDumps && singleProcessDataDump
            ? mapDataDumpToRollup(singleProcessDataDump)
            : rollupDisplay,
        preferredMetric,
        lastSegment: processSegments?.length
          ? processSegments[processSegments.length - 1]
          : null,
        isMidgardDDAEnabled,
      });

    const selectedShift = useSelectedShift();

    const shouldShowPredictedMetric =
      isPredictedFutureMetric && selectedShift?.range?.lt > Date.now();

    const metricOrEstimatedMetric = shouldShowPredictedMetric
      ? getPredictedFutureMetric({
          isOver24Hours: isTilesPageFullDay,
          dataDumpKey: preferredMetric,
          selectedShift,
          rollupDisplay,
          numProcesses: 1,
          estimatedFutureMetricPercent,
          dataDumpsObjForFullRange: singleProcessDataDump,
          isMidgardDDAEnabled,
        })
      : metricDisplay;
    const ge = segment?.range?.ge || null;

    // if the Expanded Tile + expanded tile count + expanded tile metric are all enabled,
    // AND if the tile is expanded,
    // hide this field in the tile itself (bc it becomes redundant)
    const { isCustomerWithExpandableTile } = useExpandedTileFlags(process.id);
    const { showMetricPercent } = useExpandedTileState(process.id);

    // hide the bottom info if it's redundant:
    const hideMetricOnTile =
      (!isStorybook &&
        // hide if tiles are expanded
        areProcessTilesExpanded &&
        isCustomerWithExpandableTile &&
        showMetricPercent) ||
      // hide if there's at least one tile with a lineRole
      isAtLeastOneProcessWithLineRole;

    const isTileWithoutRoleAmongTilesWithRoles =
      isAtLeastOneProcessWithLineRole &&
      (!process.lineRole || process.lineRole === LineRolesEnum.STANDARD);

    const showLineRoleDecorator =
      (process.lineRole && process.lineRole !== LineRolesEnum.STANDARD) ||
      // if only another tile has a lineRole, we still have to add the spacer here,
      // in case a tile with a decorator is on a 2nd or later row
      // (also conveniently makes just enough room for the auto-expanded metric on these tiles)
      isTileWithoutRoleAmongTilesWithRoles;

    // for tiles with lineRole, also show the metric module even while collapsed
    const showMetricPercentWhileCollapsed = Boolean(
      process.lineRole && process.lineRole !== LineRolesEnum.STANDARD
    );

    return (
      <ProcessTileContent
        isAtLeastOneProcessWithLineRole={isAtLeastOneProcessWithLineRole}
        showLineRoleDecorator={showLineRoleDecorator}
        pnlCategory={pnlCategory}
        process={process}
        callTarget={callTarget}
        callStartMs={callStartMs}
        ge={ge}
        hideMetricOnTile={hideMetricOnTile}
        shouldUseDataDumps={shouldUseDataDumps}
        metricOrEstimatedMetric={metricOrEstimatedMetric}
        isDT={isDT}
        metricUnits={metricUnits}
        isCustomerWithExpandableTile={isCustomerWithExpandableTile}
        showMetricPercentWhileCollapsed={showMetricPercentWhileCollapsed}
        statusString={statusString}
      />
    );
  }
);
ProcessTile.displayName = "ProcessTile";

export default ProcessTile;

type StatusTimeProps = {
  ge?: number | null;
  pnlCategory: string;
  displayedTime1: number; // first number of the status e.g. "1" in "1h2m"
  displayedUnit1: string; // first unit of the status e.g. "h" in "1h2m"
  displayedTime2: number; // second number of the status e.g. "2" in "1h2m"
  displayedUnit2: string; // second unit of the status e.g. the "m" in "1h2m"
  isLessThanOneMinute: boolean;
  containerWidth: number;
};

// tslint:disable-next-line: cognitive-complexity
export const StatusTime = memo(
  ({
    ge,
    pnlCategory,
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    isLessThanOneMinute,
    containerWidth,
  }: StatusTimeProps) => {
    const isOver99DaysAgo = useMemo(
      () => ge && ge < Date.now() - 99 * ONE_DAY_OF_MS,
      [ge]
    );
    const isUncollected = pnlCategory === PnlCategoriesEnum.uncollected;
    // if it's over 99 days ago, it's probably still waiting to be installed
    const hide = isOver99DaysAgo && isUncollected;
    return (
      <div
        className="process-state-elapsed"
        data-server-names={[SERVER_NAMES.askrSegments]}
      >
        {hide ? (
          <div className="inactive">--</div>
        ) : (
          <>
            <div className="process-state-elapsed-quantity">
              {ge ? (
                isLessThanOneMinute ? (
                  <span>
                    <LessThanOne containerWidth={containerWidth} />
                  </span>
                ) : (
                  displayedTime1
                )
              ) : (
                "--"
              )}
            </div>
            <div className="process-state-elapsed-units">
              {ge ? displayedUnit1 : null}
            </div>
            {ge &&
            (displayedTime2 || displayedTime2 === 0) &&
            displayedUnit2 ? (
              <>
                <div
                  className="process-state-elapsed-quantity small"
                  style={{ marginLeft: 4 }}
                >
                  {Math.abs(displayedTime2)}
                </div>
                <div className="process-state-elapsed-units">
                  {displayedUnit2}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    );
  }
);
StatusTime.displayName = "StatusTime";

const CallStatus = memo(({ callStartMs }: { callStartMs?: number | null }) => {
  const now = useForceUpdateOnInterval({ updateInterval: 1000 });
  const isStorybook = useIsStorybook();
  const callTime = useMemo(
    () =>
      truncateTime({
        durationMs:
          (isStorybook ? STORY_SHIFT_START : now) - (callStartMs ?? 0),
      }),
    [callStartMs, now, isStorybook]
  );
  return <CallStatusContent callTime={callTime} />;
});
CallStatus.displayName = "CallStatus";

const CallStatusContent = ({
  callTime,
}: {
  callTime: { displayedTime: string | number; displayedUnit: string };
}) => {
  const displayedCallTime = callTime.displayedTime;
  const displayedCallUnit = callTime.displayedUnit;

  return (
    <>
      <div className="call-icon">
        <CallIcon />
      </div>
      <div className="call-state-elapsed process-status-or-tag">
        <div className="call-state-elapsed-quantity">{displayedCallTime}</div>
        <div className="call-state-elapsed-units">{displayedCallUnit}</div>
      </div>
    </>
  );
};

export function useStatusTime({
  pnlCategory,
  processId,
  ge,
}: {
  pnlCategory: string;
  processId: string;
  ge: number | null;
}): {
  displayedTime1: number;
  displayedUnit1: string;
  displayedTime2: number;
  displayedUnit2: string;
  showCountdown: boolean;
  countDownFromMs: number | null;
  isNegative: boolean;
  isLessThanOneMinute: boolean;
} {
  const { showCountdown, countDownFromMs } = useCountdownTimer(
    pnlCategory,
    processId
  );
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const isStorybook = useIsStorybook();
  const now = useForceUpdateOnInterval({
    // slow down updates in storybook for consistent snapshots
    updateInterval: (isStorybook || isAllWorkSchedulesPage ? 10 : 1) * 1000,
  });
  const formatZoned = useFormatZoned();
  const { translate } = useFlightTranslation();

  if (ge !== 0 && !ge) {
    return {
      displayedTime1: 0,
      displayedUnit1: "",
      displayedTime2: 0,
      displayedUnit2: "",
      showCountdown,
      isNegative: false,
      countDownFromMs: 0,
      isLessThanOneMinute: false,
    };
  }
  const timeElapsed = (isStorybook ? STORY_SHIFT_START : now) - ge;
  const countDownTimeRemaining =
    (countDownFromMs || countDownFromMs === 0) && countDownFromMs - timeElapsed;

  const timeElapsedOrRemaining =
    showCountdown && countDownTimeRemaining
      ? countDownTimeRemaining
      : timeElapsed;

  const {
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    isNegative,
    isLessThanOneMinute,
  } = getFormattedDurationTimes(
    showCountdown
      ? // for countdowns, allow negative times
        timeElapsedOrRemaining
      : // for countup, minimum is 0
        Math.max(0, timeElapsedOrRemaining),
    formatZoned,
    translate
  );
  return {
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    showCountdown,
    isNegative,
    countDownFromMs,
    isLessThanOneMinute,
  };
}

/** store the tile's bounding rect, to animate from this tile to HubletView  */
export const clickedTileRectAtom = atom<{
  tileRect?: DOMRect | null;
  pnlCategory?: string | null;
  processId?: string | null;
} | null>(null);

const ProcessTileContent = memo(
  ({
    showLineRoleDecorator,
    pnlCategory,
    callTarget,
    callStartMs,
    ge,
    hideMetricOnTile,
    shouldUseDataDumps,
    metricOrEstimatedMetric,
    isDT,
    metricUnits,
    isCustomerWithExpandableTile,
    showMetricPercentWhileCollapsed,
    process,
    isAtLeastOneProcessWithLineRole,
    statusString,
  }: {
    showLineRoleDecorator: boolean;
    pnlCategory: string;
    callTarget: string | null;
    callStartMs: number | null;
    ge: number | null;
    hideMetricOnTile: boolean;
    shouldUseDataDumps: boolean;
    metricOrEstimatedMetric: number;
    isDT: boolean;
    metricUnits: string;
    isCustomerWithExpandableTile: boolean;
    showMetricPercentWhileCollapsed: boolean;
    process: Process;
    isAtLeastOneProcessWithLineRole: boolean;
    statusString: string;
  }) => {
    const { tileWidth, tileHeight } = useTileSize();

    const { translate } = useFlightTranslation();
    const [isDarkMode] = useIsDarkMode();
    const { isDesktopXlOrLarger } = useBreakpoints();
    const { innerWidth } = useWindowSize();
    const {
      isLargeTiles,
      areProcessTilesExpanded,
      tileWidthMultiplier,
      tileScaleMultiplier = 1,
    } = useSettings();
    const isCall = callTarget; /* && callStartMs */

    const [, setClickedTileRect] = useAtom(clickedTileRectAtom);
    const processTileRef = useRef<HTMLButtonElement>(null);

    const isLongTagName = translate(statusString).length > 26; // 26 ~= 2 lines of text in large tiles mode
    const isAdminUser = useIsAdmin();
    return (
      <ProcessTileStyles
        data-component-name={"Process Tile"}
        {...{
          $isLargeTiles: isLargeTiles,
          $tileWidth: tileWidth,
          $tileWidthMultiplier: tileWidthMultiplier,
          $tileScaleMultiplier: tileScaleMultiplier,
          $tileHeight: tileHeight,
          $isDesktopXlOrLarger: isDesktopXlOrLarger,
          $areProcessTilesExpanded: areProcessTilesExpanded,
          $hideMetricOnTile: hideMetricOnTile,
          $isLongProcessName:
            process.name.length > MAX_CHARACTERS_BEFORE_SHRINK,
          $isVeryLongProcessName:
            process.name.length > MAX_CHARACTERS_BEFORE_LEFT_ALIGN,
          $isDarkMode: isDarkMode,
        }}
      >
        <Button
          ref={processTileRef}
          className={`process-tile process-tile-${pnlCategory} process-tile-${process.id} ${areProcessTilesExpanded ? "expanded" : ""}`}
          data-server-names={[SERVER_NAMES.askrSegments]}
          onClick={() => {
            setClickedTileRect({
              tileRect: processTileRef.current?.getBoundingClientRect(),
              pnlCategory: pnlCategory,
              processId: process.id,
            });
          }}
        >
          <Tooltip
            title={isAdminUser ? process.id : ""}
            disableInteractive={true}
          >
            <div
              className="process-name"
              data-server-names={[SERVER_NAMES.midgardProcessGroups]}
            >
              <ResponsiveTypography
                text={process.name}
                maxFontSize={isDesktopXlOrLarger ? innerWidth / 13 : 42}
                minFontSize={
                  process.name.length < 12 ? 16 : 200 / process.name.length
                } // ensure text is visible before responsiveness kicks in
              />
            </div>
          </Tooltip>
          {isCall ? (
            <CallStatus callStartMs={callStartMs} />
          ) : (
            <StatusTimeWrapper
              {...{
                pnlCategory,
                process,
                ge,
                hideMetricOnTile,
                metricOrEstimatedMetric,
                metricUnits,
                isDT,
                isLargeTiles,
                statusString,
                tileWidth,
                isLongTagName,
                shouldUseDataDumps,
              }}
            />
          )}
        </Button>
        {showLineRoleDecorator ? (
          <LineRoleDecorator lineRole={process.lineRole} />
        ) : null}

        {(isCustomerWithExpandableTile || showMetricPercentWhileCollapsed) && (
          <ExpandedTileContent
            {...{
              process,
              isAtLeastOneProcessWithLineRole,
              showMetricPercentWhileCollapsed,
            }}
          >
            <Button
              ref={processTileRef}
              className={`process-tile process-tile-${pnlCategory} process-tile-${process.id} ${areProcessTilesExpanded ? "expanded" : ""} ${isLargeTiles ? "large-tiles" : ""}`}
              data-server-names={[SERVER_NAMES.askrSegments]}
              onClick={() => {
                setClickedTileRect({
                  tileRect: processTileRef.current?.getBoundingClientRect(),
                  pnlCategory: pnlCategory,
                  processId: process.id,
                });
              }}
            >
              <Tooltip
                title={isAdminUser ? process.id : ""}
                disableInteractive={true}
              >
                <div
                  className="process-name"
                  data-server-names={[SERVER_NAMES.midgardProcessGroups]}
                >
                  <ResponsiveTypography
                    text={process.name}
                    maxFontSize={isDesktopXlOrLarger ? innerWidth / 13 : 42}
                    minFontSize={
                      process.name.length < 12 ? 16 : 200 / process.name.length
                    } // ensure text is visible before responsiveness kicks in
                  />
                </div>
              </Tooltip>
            </Button>
          </ExpandedTileContent>
        )}
      </ProcessTileStyles>
    );
  }
);
ProcessTileContent.displayName = "ProcessTileContent";

function StatusTimeWrapper({
  pnlCategory,
  process,
  ge,
  hideMetricOnTile,
  metricOrEstimatedMetric,
  metricUnits,
  isDT,
  isLargeTiles,
  statusString,
  tileWidth,
  isLongTagName,
  shouldUseDataDumps,
}: {
  pnlCategory: string;
  process: Process;
  ge: number | null;
  hideMetricOnTile: boolean;
  metricOrEstimatedMetric: number;
  metricUnits: string;
  isDT: boolean;
  isLargeTiles: boolean;
  statusString: string;
  tileWidth: number;
  isLongTagName: boolean;
  shouldUseDataDumps: boolean;
}) {
  const {
    showCountdown,
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    isNegative,
    isLessThanOneMinute,
  } = useStatusTime({ pnlCategory, processId: process.id, ge });

  return (
    <StatusTimeContent
      {...{
        pnlCategory,
        process,
        ge,
        hideMetricOnTile,
        metricOrEstimatedMetric,
        metricUnits,
        isDT,
        isLargeTiles,
        statusString,
        tileWidth,
        isLongTagName,
        shouldUseDataDumps,

        showCountdown,
        displayedTime1,
        displayedUnit1,
        displayedTime2,
        displayedUnit2,
        isNegative,
        isLessThanOneMinute,
      }}
    />
  );
}
const StatusTimeContent = memo(
  ({
    pnlCategory,
    process,
    ge,
    hideMetricOnTile,
    metricOrEstimatedMetric,
    metricUnits,
    isDT,
    isLargeTiles,
    statusString,
    tileWidth,
    isLongTagName,
    shouldUseDataDumps,

    showCountdown,
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    isNegative,
    isLessThanOneMinute,
  }: {
    pnlCategory: string;
    process: Process;
    ge: number | null;
    hideMetricOnTile: boolean;
    metricOrEstimatedMetric: number;
    metricUnits: string;
    isDT: boolean;
    isLargeTiles: boolean;
    statusString: string;
    tileWidth: number;
    isLongTagName: boolean;
    shouldUseDataDumps: boolean;

    showCountdown: boolean;
    displayedTime1: number;
    displayedUnit1: string;
    displayedTime2: number;
    displayedUnit2: string;
    isNegative: boolean;
    isLessThanOneMinute: boolean;
  }) => {
    useEffect(() => {
      const processTile = document.querySelector<HTMLElement>(
        `.process-tile-${process.id}`
      );
      if (!processTile) {
        return;
      }
      if (
        isNegative &&
        !Array.from(processTile.classList).includes("negative")
      ) {
        processTile.classList.add("negative");
      } else if (
        !isNegative &&
        Array.from(processTile.classList).includes("negative")
      ) {
        processTile.classList.remove("negative");
      }
    }, [isNegative, process.id]);

    const { translate } = useFlightTranslation();

    return (
      <>
        <div className="process-state">
          <StatusTime
            ge={ge}
            pnlCategory={pnlCategory}
            isLessThanOneMinute={isLessThanOneMinute}
            displayedTime1={displayedTime1}
            displayedUnit1={displayedUnit1}
            displayedTime2={displayedTime2}
            displayedUnit2={displayedUnit2}
            containerWidth={isLargeTiles ? tileWidth * 2 : TILE_WIDTH_PX_LG}
            /* ? not sure why this multiplication is necessary */
          />
          <div
            className={`process-status-or-tag ${
              isLongTagName ? "long-tag-name" : ""
            } ${isLargeTiles ? "large-tiles" : ""}`}
            data-server-names={[SERVER_NAMES.askrSegments]}
          >
            {showCountdown
              ? translate("remaining")
              : translate(statusString).toLowerCase()}
          </div>
        </div>
        {hideMetricOnTile ? null : (
          <div
            className="process-metric"
            data-server-names={[
              shouldUseDataDumps
                ? SERVER_NAMES.midgardDataDumpsFullRangeByProcess
                : SERVER_NAMES.askrAggregates,
            ]}
          >
            <span className="process-metric-value">
              {metricOrEstimatedMetric}
              <span className="process-metric-units">
                {isDT ? "%" : metricUnits || "parts"}
              </span>
            </span>
          </div>
        )}
      </>
    );
  }
);
StatusTimeContent.displayName = "StatusTimeContent";
